import { useCallback, useSyncExternalStore } from 'react';

const getStorageKey = (userId: string) => `ai-chat-onboarding-complete-${userId}`;

const localListeners = new Set<() => void>();

const subscribe = (callback: () => void) => {
    if (typeof window === 'undefined') return () => {};
    window.addEventListener('storage', callback);
    localListeners.add(callback);
    return () => {
        window.removeEventListener('storage', callback);
        localListeners.delete(callback);
    };
};

export const useOnboardingComplete = (userId: string) => {
    const key = getStorageKey(userId);

    const isComplete = useSyncExternalStore(
        subscribe,
        () => {
            try {
                return localStorage.getItem(key) === 'true';
            }
            catch {
                return false;
            }
        },
        // Server snapshot: there is no localStorage on the server, and onboarding state is
        // per-device anyway. Without this React discards the whole boundary.
        () => false,
    );

    const markComplete = useCallback(() => {
        try {
            localStorage.setItem(key, 'true');
            localListeners.forEach(listener => listener());
        }
        catch (error) {
            console.error('Unable to persist completion status', error);
        }
    }, [key]);

    return { isComplete, markComplete };
};
